import { useTranslation } from 'react-i18next';
import { Card, Icon, Input } from 'semantic-ui-react';
import styles from '../styles/Activity.module.scss';
import GridColumnInputWrapper from '@components/common/GridColumnInputWrapper';
import { useEffect, useRef, useState } from 'react';
import { UserDTO } from '@behavior/init/dto/UserDTO';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import UserDateInput from '../UserDateInput';
import { useFormContext } from 'react-hook-form';
import CommentsContainer from '../CommentsContainer';
import usePostComment from '@behavior/activity/usePostComment';
import { ActionCommentDTO } from '@behavior/activity/dto/ActionCommentDTO';
import { ActivityInitialDataResponse } from '@components/Activity/types/ActivityInitialDataResponse';
import { Label } from 'semantic-ui-react';
import lodash from 'lodash';
import { useIsFetching } from 'react-query';
import { QueryKey } from '@behavior/QueryKey';
import TextArea from '@components/common/TextArea';

type Props = {
  onRemove?: () => void;
  actionContent: ActionDTO;
  setActionCardContent?: (actionContent: ActionDTO) => void;
  initialData?: ActivityInitialDataResponse;
  disabled?: boolean;
  viewOnly?: boolean;
  index: number;
};

const ActionCard = ({
  onRemove,
  actionContent,
  setActionCardContent,
  initialData,
  disabled,
  index,
}: Props) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const currentUser = initialData?.currentUser;
  const isFetchingAction = useIsFetching([QueryKey.ACTION]);
  const [userDateValidation, setUserDateValidation] = useState(0);
  const [userValueWithoutPersonalData, setUserValueWithoutPersonalData] = useState(
    actionContent.assignedUser
  );
  const isActionCompleted: boolean =
    actionContent.completionDte != undefined && actionContent.completionDte.length > 0;

  const setDefaultUser = (clear: boolean) => {
    if (setActionCardContent) {
      setActionCardContent({
        ...actionContent,
        assignedUser: clear ? undefined : currentUser,
      });
    }
  };

  const ACTION_BASE_KEY = `actions[${index}]`;
  const ACTION_DESCRIPTION_KEY = ACTION_BASE_KEY + '.description';

  const descriptionRegister = register(ACTION_DESCRIPTION_KEY, {
    required: t(`activity.form.error.description.required`),
    minLength: {
      value: 5,
      message: t('activity.form.error.field.min.val.5'),
    },
    maxLength: {
      value: 1000,
      message: t('activity.form.error.field.max.val.1000'),
    },
  });

  useEffect(() => {
    if (actionContent.description) {
      setValue(ACTION_DESCRIPTION_KEY, actionContent.description);
    }
  }, [isFetchingAction]);

  const updateUserDateFieldsValidation = (selectedOption?: UserDTO) => {
    setUserDateValidation(userDateValidation + 1);
    if (setActionCardContent) {
      setActionCardContent({
        ...actionContent,
        assignedUser: selectedOption,
      });
    }
  };

  const updateDataField = (selectedOption: any) => {
    if (setActionCardContent)
      setActionCardContent({
        ...actionContent,
        completionDte: selectedOption,
      });
  };

  const showDeadline = !actionContent.completionDte;

  const setFieldVal = (val: string, date: any) => {
    if (setActionCardContent) {
      setActionCardContent({
        ...actionContent,
        [showDeadline ? 'deadlineDte' : 'completionDte']: date,
      });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (setActionCardContent) {
      setActionCardContent({
        ...actionContent,
        description: e.target.value,
      });
    }

    descriptionRegister.onChange(e);
  };

  const postComment = usePostComment(actionContent.id || 0);

  const addComment = (comment: string[]) => {
    const newComment: ActionCommentDTO = {
      description: comment[0],
      createDte: Date.now().toString(),
    };

    postComment(newComment).then(action => {
      setActionCardContent?.(action);
    });
  };

  return (
    <Card className={styles.cardText}>
      <div className={styles.basicDiv}>
        {isActionCompleted && (
          <Label as="a" color="green" ribbon size="medium">
            {t('action.label.completed')}
          </Label>
        )}
        <div className={styles.flexContainerActivity}>
          <div className={styles.chemicalTextHeader}>
            {t('action.form.details.page.container.action.required.container.title')}
          </div>
          {!disabled && (
            <div className={styles.closeButton}>
              <Icon name="close" size="small" color="blue" onClick={onRemove} />
            </div>
          )}
        </div>
        <div>
          <div className={styles.checkboxContainerText}>
            <GridColumnInputWrapper
              value={actionContent.description}
              rows={1}
              disabled={disabled}
              label={t('action.form.details.page.container.action.required.container.label')}
              onChange={handleDescriptionChange}
              specialRequiredCondition
              render={(props: any) => (
                <div className={disabled ? 'no-resize' : ''}>
                  <div className={styles.errorMessage}>
                    {lodash.get(errors, ACTION_DESCRIPTION_KEY)?.message?.toString()}
                  </div>
                  <TextArea
                    register={descriptionRegister}
                    disabled={disabled}
                    value={actionContent.description}
                    rows={2}
                    onChange={handleDescriptionChange}
                  />
                </div>
              )}
            />
          </div>
          <UserDateInput
            setDefaultUser={setDefaultUser}
            updateUserDateFieldsValidation={updateUserDateFieldsValidation}
            updateDataField={updateDataField}
            setFieldVal={setFieldVal}
            userVal={actionContent.assignedUser}
            userValueWithoutPersonalData={userValueWithoutPersonalData}
            setUserValueWithoutPersonalData={setUserValueWithoutPersonalData}
            dteVal={showDeadline ? actionContent.deadlineDte : actionContent.completionDte}
            userLabel={t('action.form.details.page.container.action.required.specify.resolver')}
            dateLabel={
              showDeadline
                ? t('action.form.details.page.container.action.required.specify.date.for.deadline')
                : t('action.form.details.page.container.action.required.specify.date.of.completion')
            }
            nameUser={ACTION_BASE_KEY + '.assignedUser'}
            dteName={ACTION_BASE_KEY + (showDeadline ? '.deadlineDte' : '.assignedDte')}
            disabled={disabled}
            isDateRequired={true}
          />
        </div>
      </div>
      <CommentsContainer actionComments={actionContent?.comments} addComment={addComment} />
    </Card>
  );
};

export default ActionCard;
